// c-accordion-exclusive.js
// Roots custom: "only one open" sibling-auto-close for <accordion-custom> groups.
//
// Update-proof by construction: zero edits to the vanilla Horizon
// accordion-custom.js. Replaces a former +21-line core modification that lived
// in the element's click handler.
//
// We delegate a CAPTURE-phase 'click' on <summary> elements. Capture runs before
// the native <details> toggle, so details.open still reflects the pre-click state.
// Acting on user clicks — exactly like the original handleClick — means we do NOT
// fire on PROGRAMMATIC opens (default-open on load, breakpoint changes); a document
// 'toggle' listener would have, which could wrongly auto-close multi-open groups
// such as faceted filters. When a click is about to OPEN an accordion, close the
// open siblings in its group, applying the `.accordion-closing` class consumed by
// assets/roots-custom.css. Keyboard activation (Enter/Space on a focused summary)
// dispatches a click too, so it is covered.
//
// Loaded as a module from snippets/scripts.liquid (a Roots edit), after accordion-custom.js.

import { isMobileBreakpoint } from '@theme/utilities';

const GROUP_SELECTOR = '.accordion, [data-accordion-group], .details-group';
const CLOSING_CLASS = 'accordion-closing';
const CLEANUP_FALLBACK_MS = 400;

/** The host accordion is inert on the current breakpoint — match core behavior. */
function isHostDisabledHere(host) {
  const mobile = isMobileBreakpoint();
  return (
    (mobile && host.dataset.disableOnMobile === 'true') ||
    (!mobile && host.dataset.disableOnDesktop === 'true')
  );
}

function closeSibling(siblingDetails) {
  // Add class to enable the closing transition even without :focus-within
  siblingDetails.classList.add(CLOSING_CLASS);
  requestAnimationFrame(() => {
    siblingDetails.open = false;
    const cleanup = () => siblingDetails.classList.remove(CLOSING_CLASS);
    // Clean up after the transition completes…
    siblingDetails.addEventListener('transitionend', cleanup, { once: true });
    // …or via fallback if transitionend never fires (e.g. reduced motion).
    setTimeout(cleanup, CLEANUP_FALLBACK_MS);
  });
}

function onSummaryClickCapture(event) {
  const summary = event.target.closest('summary');
  if (!summary) return;

  const details = summary.closest('details');
  // Only when the click is about to OPEN it (currently closed). Mirrors the
  // original `if (!this.details.open)`: ignores clicks that close an item, and —
  // being click-driven — never fires on programmatic opens.
  if (!details || details.open) return;

  const host = details.closest('accordion-custom');
  if (!host) return; // only act on Horizon accordions
  if (isHostDisabledHere(host)) return; // disabled breakpoint: core preventDefaults the toggle

  const group = host.closest(GROUP_SELECTOR) || host.parentElement;
  if (!group) return;

  group.querySelectorAll('accordion-custom').forEach((sibling) => {
    if (sibling === host) return;
    const siblingDetails = sibling.querySelector('details');
    if (siblingDetails && siblingDetails.open) closeSibling(siblingDetails);
  });
}

document.addEventListener('click', onSummaryClickCapture, { capture: true });
